import { useEffect, useRef, useState } from "react";
import useFetch from "../../Hooks/useFetch";
import styles from "./assets/css/DealerLocator.module.css";
import { CSSTransition } from "react-transition-group";
import magnifyingGlassIcon from "./assets/img/magnifying-glass.svg";
import customLocationPin from "./assets/img/user_location_pin.png";

import {
  GoogleMap,
  MarkerF,
  useLoadScript,
  Autocomplete,
  InfoWindowF,
} from "@react-google-maps/api";

const libraries = ["places", "geometry"];
const maxMarkerBounds = 8;

const DealerLocator = () => {
  const { data: brands, loading, error } = useFetch("dealer_brand");
  const { data: dealers } = useFetch("dealers");
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [dealerSearchInProp, setDealerSearchInProp] = useState(true);
  const [dealerResultsInProp, setDealerResultsInProp] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);

  const dealerSearchNodeRef = useRef(null);
  const dealerResultsNodeRef = useRef(null);
  const autocompleteRef = useRef();
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const { isLoaded: mapLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || null,
    libraries,
  });

  const [visibleDealers, setVisibleDealers] = useState(null);
  const [markers, setMarkers] = useState(null);

  const onLoadMap = (map) => {
    mapRef.current = map;
    setMapBounds();
  };

  const handleSearchClick = async () => {
    if (dealerSearchInProp) {
      setDealerSearchInProp(false);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    const userLocation = searchQuery.geometry.location;
    const distances = markers.map((marker) => {
      const markerLocation = new window.google.maps.LatLng(
        marker.lat,
        marker.lng
      );

      const distance =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          userLocation,
          markerLocation
        );
      return { ...marker, distance };
    });

    const sortedLabeledMarkers = await distances
      .sort((a, b) => a.distance - b.distance)
      .map((marker, labelIndex) => {
        const label = (labelIndex + 1).toString();
        return { ...marker, label };
      });

    setMarkers(() => sortedLabeledMarkers);
    setMapBounds();
  };

  const setMapBounds = () => {
    if (!mapRef.current) return;
    if (markers?.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      // Add users location as LatLngBound
      bounds.extend(
        new window.google.maps.LatLng(
          searchQuery.geometry.location.lat(),
          searchQuery.geometry.location.lng()
        )
      );

      // Add first n markers sorted by distance.
      markers.forEach((marker, index) => {
        if (index >= maxMarkerBounds) return;
        bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
      });

      mapRef.current.fitBounds(bounds);
    }
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place === undefined || !place.geometry) return;
    setSearchQuery(place);
  };

  const onLoadAutocomplete = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  // Get available dealers based on selected brand.
  useEffect(() => {
    if (!selectedBrandId) return setVisibleDealers(null);
    if (!dealers) return;

    const availableDealers = [];
    for (let i = 0; i < dealers.length; i++) {
      for (let j = 0; j < dealers[i].acf.dealer_brand.length; j++) {
        if (dealers[i].acf.dealer_brand[j].ID === selectedBrandId) {
          if (dealers[i].acf.google_map === null) continue;
          availableDealers.push(dealers[i]);
        }
      }
    }

    setVisibleDealers(availableDealers);
  }, [selectedBrandId, dealers]);

  const handleBrandSelect = (id) => {
    if (selectedBrandId === id) {
      setSelectedBrandId(null);
      setSearchQuery(null);
      setErrorMessage("");
    } else setSelectedBrandId(id);
  };

  // Create map markers for assigned dealers
  useEffect(() => {
    if (!visibleDealers) return;
    if (visibleDealers.length <= 0)
      return setErrorMessage("No dealers found for selected brand");

    setErrorMessage("");
    const markers = visibleDealers.map((dealer) => ({
      dealerName: dealer.title.rendered,
      address: dealer.acf.google_map.address,
      contactNumber: dealer.acf.contact_number,
      email: dealer.acf.email_address,
      website: dealer.acf.website_address,
      lat: dealer.acf.google_map.lat,
      lng: dealer.acf.google_map.lng,
    }));

    setMarkers(markers);
  }, [visibleDealers]);

  const handleChangeBrandClick = () => {
    setDealerResultsInProp(false);
    setSelectedBrandId(null);
    setSearchQuery(null);
  };

  const handleViewMapClick = (marker) => {
    mapRef.current.panTo({
      lat: marker.lat,
      lng: marker.lng,
    });
    mapRef.current.setZoom(15);
    if (window.screen.width < 1024) mapContainerRef.current.scrollIntoView();
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      return e.preventDefault(); // Prevent form submission
    }
  };

  return (
    <div className={styles.backgroundContainer}>
      <CSSTransition
        nodeRef={dealerSearchNodeRef}
        in={dealerSearchInProp}
        timeout={1000}
        classNames={{
          appear: styles.dealerSearchAppear,
          appearActive: styles.dealerSearchActiveAppear,
          appearDone: styles.dealerSearchDoneAppear,
          enter: styles.dealerSearchEnter,
          enterActive: styles.dealerSearchActiveEnter,
          enterDone: styles.dealerSearchDoneEnter,
          exit: styles.dealerSearchExit,
          exitActive: styles.dealerSearchActiveExit,
          exitDone: styles.dealerSearchDoneExit,
        }}
        unmountOnExit={true}
        mountOnEnter={true}
        onExited={() => setDealerResultsInProp(true)}
      >
        <div
          ref={dealerSearchNodeRef}
          className={styles.dealerLocatorContainer}
        >
          <div className={styles.dealerLocatorTitle}>
            <h1>Discover your nearest distributor or authorized dealer</h1>
          </div>
          <div className={styles.dealerLocatorSelector}>
            <div className={styles.dealerLocatorSelectorContent}>
              <p>
                Choose the brand first, then enter a location to find you
                nearest Technical Touch outlet.
              </p>
              {/* Brand Selector */}
              <div className={styles.brandsContainer}>
                {loading ? (
                  <>
                    <button className={styles.brand}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          background: "#ffffff00",
                          display: "block",
                        }}
                        width="100px"
                        height="100px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <g transform="translate(50 50)">
                          <g>
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              values="0;40"
                              keyTimes="0;1"
                              dur="0.5s"
                              repeatCount="indefinite"
                            ></animateTransform>
                            <path
                              d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 26.12715009048092 14.743541913308446 L26.12715009048092 14.743541913308446 L32.25550563543275 19.885842790800762 L25.18484192888081 28.312331665109514 L19.056486383928988 23.1700307876172 A30 30 0 0 1 10.537592076579571 28.08841670916336 L10.537592076579571 28.08841670916336 L11.926777497915015 35.96687873326103 L1.0938922147807286 37.87700868759726 L-0.2952932065547146 29.998546663499596 A30 30 0 0 1 -9.98262238224421 28.290409158821245 L-9.98262238224421 28.290409158821245 L-13.98262238224421 35.218612389096755 L-23.50890182387304 29.71861238909675 L-19.50890182387304 22.79040915882124 A30 30 0 0 1 -25.831856883926196 15.255004750191155 L-25.831856883926196 15.255004750191155 L-33.349397850213464 17.991165896796506 L-37.111619426795826 7.654547068151507 L-29.59407846050856 4.918385921546156 A30 30 0 0 1 -29.59407846050856 -4.9183859215461485 L-29.59407846050856 -4.9183859215461485 L-37.111619426795826 -7.654547068151498 L-33.349397850213464 -17.991165896796502 L-25.8318568839262 -15.255004750191151 A30 30 0 0 1 -19.508901823873053 -22.79040915882123 L-19.508901823873053 -22.79040915882123 L-23.508901823873057 -29.718612389096737 L-13.982622382244227 -35.21861238909675 L-9.982622382244223 -28.29040915882124 A30 30 0 0 1 -0.29529320655473523 -29.998546663499596 L-0.29529320655473523 -29.998546663499596 L1.0938922147807042 -37.87700868759726 L11.926777497915003 -35.96687873326103 L10.537592076579564 -28.08841670916336 A30 30 0 0 1 19.056486383928977 -23.170030787617215 L19.056486383928977 -23.170030787617215 L25.1848419288808 -28.312331665109532 L32.25550563543274 -19.885842790800766 L26.127150090480917 -14.743541913308448 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                              fill="#ed2024"
                            ></path>
                          </g>
                        </g>
                      </svg>
                      <p className={styles.loadingText}>Loading</p>
                    </button>
                    <button className={styles.brand}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          background: "#ffffff00",
                          display: "block",
                        }}
                        width="100px"
                        height="100px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <g transform="translate(50 50)">
                          <g>
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              values="0;40"
                              keyTimes="0;1"
                              dur="0.5s"
                              repeatCount="indefinite"
                            ></animateTransform>
                            <path
                              d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 26.12715009048092 14.743541913308446 L26.12715009048092 14.743541913308446 L32.25550563543275 19.885842790800762 L25.18484192888081 28.312331665109514 L19.056486383928988 23.1700307876172 A30 30 0 0 1 10.537592076579571 28.08841670916336 L10.537592076579571 28.08841670916336 L11.926777497915015 35.96687873326103 L1.0938922147807286 37.87700868759726 L-0.2952932065547146 29.998546663499596 A30 30 0 0 1 -9.98262238224421 28.290409158821245 L-9.98262238224421 28.290409158821245 L-13.98262238224421 35.218612389096755 L-23.50890182387304 29.71861238909675 L-19.50890182387304 22.79040915882124 A30 30 0 0 1 -25.831856883926196 15.255004750191155 L-25.831856883926196 15.255004750191155 L-33.349397850213464 17.991165896796506 L-37.111619426795826 7.654547068151507 L-29.59407846050856 4.918385921546156 A30 30 0 0 1 -29.59407846050856 -4.9183859215461485 L-29.59407846050856 -4.9183859215461485 L-37.111619426795826 -7.654547068151498 L-33.349397850213464 -17.991165896796502 L-25.8318568839262 -15.255004750191151 A30 30 0 0 1 -19.508901823873053 -22.79040915882123 L-19.508901823873053 -22.79040915882123 L-23.508901823873057 -29.718612389096737 L-13.982622382244227 -35.21861238909675 L-9.982622382244223 -28.29040915882124 A30 30 0 0 1 -0.29529320655473523 -29.998546663499596 L-0.29529320655473523 -29.998546663499596 L1.0938922147807042 -37.87700868759726 L11.926777497915003 -35.96687873326103 L10.537592076579564 -28.08841670916336 A30 30 0 0 1 19.056486383928977 -23.170030787617215 L19.056486383928977 -23.170030787617215 L25.1848419288808 -28.312331665109532 L32.25550563543274 -19.885842790800766 L26.127150090480917 -14.743541913308448 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                              fill="#ed2024"
                            ></path>
                          </g>
                        </g>
                      </svg>
                      <p className={styles.loadingText}>Loading</p>
                    </button>
                    <button className={styles.brand}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          background: "#ffffff00",
                          display: "block",
                        }}
                        width="100px"
                        height="100px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <g transform="translate(50 50)">
                          <g>
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              values="0;40"
                              keyTimes="0;1"
                              dur="0.5s"
                              repeatCount="indefinite"
                            ></animateTransform>
                            <path
                              d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 26.12715009048092 14.743541913308446 L26.12715009048092 14.743541913308446 L32.25550563543275 19.885842790800762 L25.18484192888081 28.312331665109514 L19.056486383928988 23.1700307876172 A30 30 0 0 1 10.537592076579571 28.08841670916336 L10.537592076579571 28.08841670916336 L11.926777497915015 35.96687873326103 L1.0938922147807286 37.87700868759726 L-0.2952932065547146 29.998546663499596 A30 30 0 0 1 -9.98262238224421 28.290409158821245 L-9.98262238224421 28.290409158821245 L-13.98262238224421 35.218612389096755 L-23.50890182387304 29.71861238909675 L-19.50890182387304 22.79040915882124 A30 30 0 0 1 -25.831856883926196 15.255004750191155 L-25.831856883926196 15.255004750191155 L-33.349397850213464 17.991165896796506 L-37.111619426795826 7.654547068151507 L-29.59407846050856 4.918385921546156 A30 30 0 0 1 -29.59407846050856 -4.9183859215461485 L-29.59407846050856 -4.9183859215461485 L-37.111619426795826 -7.654547068151498 L-33.349397850213464 -17.991165896796502 L-25.8318568839262 -15.255004750191151 A30 30 0 0 1 -19.508901823873053 -22.79040915882123 L-19.508901823873053 -22.79040915882123 L-23.508901823873057 -29.718612389096737 L-13.982622382244227 -35.21861238909675 L-9.982622382244223 -28.29040915882124 A30 30 0 0 1 -0.29529320655473523 -29.998546663499596 L-0.29529320655473523 -29.998546663499596 L1.0938922147807042 -37.87700868759726 L11.926777497915003 -35.96687873326103 L10.537592076579564 -28.08841670916336 A30 30 0 0 1 19.056486383928977 -23.170030787617215 L19.056486383928977 -23.170030787617215 L25.1848419288808 -28.312331665109532 L32.25550563543274 -19.885842790800766 L26.127150090480917 -14.743541913308448 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                              fill="#ed2024"
                            ></path>
                          </g>
                        </g>
                      </svg>
                      <p className={styles.loadingText}>Loading</p>
                    </button>
                    <button className={styles.brand}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          background: "#ffffff00",
                          display: "block",
                        }}
                        width="100px"
                        height="100px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <g transform="translate(50 50)">
                          <g>
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              values="0;40"
                              keyTimes="0;1"
                              dur="0.5s"
                              repeatCount="indefinite"
                            ></animateTransform>
                            <path
                              d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 26.12715009048092 14.743541913308446 L26.12715009048092 14.743541913308446 L32.25550563543275 19.885842790800762 L25.18484192888081 28.312331665109514 L19.056486383928988 23.1700307876172 A30 30 0 0 1 10.537592076579571 28.08841670916336 L10.537592076579571 28.08841670916336 L11.926777497915015 35.96687873326103 L1.0938922147807286 37.87700868759726 L-0.2952932065547146 29.998546663499596 A30 30 0 0 1 -9.98262238224421 28.290409158821245 L-9.98262238224421 28.290409158821245 L-13.98262238224421 35.218612389096755 L-23.50890182387304 29.71861238909675 L-19.50890182387304 22.79040915882124 A30 30 0 0 1 -25.831856883926196 15.255004750191155 L-25.831856883926196 15.255004750191155 L-33.349397850213464 17.991165896796506 L-37.111619426795826 7.654547068151507 L-29.59407846050856 4.918385921546156 A30 30 0 0 1 -29.59407846050856 -4.9183859215461485 L-29.59407846050856 -4.9183859215461485 L-37.111619426795826 -7.654547068151498 L-33.349397850213464 -17.991165896796502 L-25.8318568839262 -15.255004750191151 A30 30 0 0 1 -19.508901823873053 -22.79040915882123 L-19.508901823873053 -22.79040915882123 L-23.508901823873057 -29.718612389096737 L-13.982622382244227 -35.21861238909675 L-9.982622382244223 -28.29040915882124 A30 30 0 0 1 -0.29529320655473523 -29.998546663499596 L-0.29529320655473523 -29.998546663499596 L1.0938922147807042 -37.87700868759726 L11.926777497915003 -35.96687873326103 L10.537592076579564 -28.08841670916336 A30 30 0 0 1 19.056486383928977 -23.170030787617215 L19.056486383928977 -23.170030787617215 L25.1848419288808 -28.312331665109532 L32.25550563543274 -19.885842790800766 L26.127150090480917 -14.743541913308448 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                              fill="#ed2024"
                            ></path>
                          </g>
                        </g>
                      </svg>
                      <p className={styles.loadingText}>Loading</p>
                    </button>
                    <button className={styles.brand}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          background: "#ffffff00",
                          display: "block",
                        }}
                        width="100px"
                        height="100px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <g transform="translate(50 50)">
                          <g>
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              values="0;40"
                              keyTimes="0;1"
                              dur="0.5s"
                              repeatCount="indefinite"
                            ></animateTransform>
                            <path
                              d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 26.12715009048092 14.743541913308446 L26.12715009048092 14.743541913308446 L32.25550563543275 19.885842790800762 L25.18484192888081 28.312331665109514 L19.056486383928988 23.1700307876172 A30 30 0 0 1 10.537592076579571 28.08841670916336 L10.537592076579571 28.08841670916336 L11.926777497915015 35.96687873326103 L1.0938922147807286 37.87700868759726 L-0.2952932065547146 29.998546663499596 A30 30 0 0 1 -9.98262238224421 28.290409158821245 L-9.98262238224421 28.290409158821245 L-13.98262238224421 35.218612389096755 L-23.50890182387304 29.71861238909675 L-19.50890182387304 22.79040915882124 A30 30 0 0 1 -25.831856883926196 15.255004750191155 L-25.831856883926196 15.255004750191155 L-33.349397850213464 17.991165896796506 L-37.111619426795826 7.654547068151507 L-29.59407846050856 4.918385921546156 A30 30 0 0 1 -29.59407846050856 -4.9183859215461485 L-29.59407846050856 -4.9183859215461485 L-37.111619426795826 -7.654547068151498 L-33.349397850213464 -17.991165896796502 L-25.8318568839262 -15.255004750191151 A30 30 0 0 1 -19.508901823873053 -22.79040915882123 L-19.508901823873053 -22.79040915882123 L-23.508901823873057 -29.718612389096737 L-13.982622382244227 -35.21861238909675 L-9.982622382244223 -28.29040915882124 A30 30 0 0 1 -0.29529320655473523 -29.998546663499596 L-0.29529320655473523 -29.998546663499596 L1.0938922147807042 -37.87700868759726 L11.926777497915003 -35.96687873326103 L10.537592076579564 -28.08841670916336 A30 30 0 0 1 19.056486383928977 -23.170030787617215 L19.056486383928977 -23.170030787617215 L25.1848419288808 -28.312331665109532 L32.25550563543274 -19.885842790800766 L26.127150090480917 -14.743541913308448 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                              fill="#ed2024"
                            ></path>
                          </g>
                        </g>
                      </svg>
                      <p className={styles.loadingText}>Loading</p>
                    </button>
                    <button className={styles.brand}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          background: "#ffffff00",
                          display: "block",
                        }}
                        width="100px"
                        height="100px"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="xMidYMid"
                      >
                        <g transform="translate(50 50)">
                          <g>
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              values="0;40"
                              keyTimes="0;1"
                              dur="0.5s"
                              repeatCount="indefinite"
                            ></animateTransform>
                            <path
                              d="M29.491524206117255 -5.5 L37.491524206117255 -5.5 L37.491524206117255 5.5 L29.491524206117255 5.5 A30 30 0 0 1 26.12715009048092 14.743541913308446 L26.12715009048092 14.743541913308446 L32.25550563543275 19.885842790800762 L25.18484192888081 28.312331665109514 L19.056486383928988 23.1700307876172 A30 30 0 0 1 10.537592076579571 28.08841670916336 L10.537592076579571 28.08841670916336 L11.926777497915015 35.96687873326103 L1.0938922147807286 37.87700868759726 L-0.2952932065547146 29.998546663499596 A30 30 0 0 1 -9.98262238224421 28.290409158821245 L-9.98262238224421 28.290409158821245 L-13.98262238224421 35.218612389096755 L-23.50890182387304 29.71861238909675 L-19.50890182387304 22.79040915882124 A30 30 0 0 1 -25.831856883926196 15.255004750191155 L-25.831856883926196 15.255004750191155 L-33.349397850213464 17.991165896796506 L-37.111619426795826 7.654547068151507 L-29.59407846050856 4.918385921546156 A30 30 0 0 1 -29.59407846050856 -4.9183859215461485 L-29.59407846050856 -4.9183859215461485 L-37.111619426795826 -7.654547068151498 L-33.349397850213464 -17.991165896796502 L-25.8318568839262 -15.255004750191151 A30 30 0 0 1 -19.508901823873053 -22.79040915882123 L-19.508901823873053 -22.79040915882123 L-23.508901823873057 -29.718612389096737 L-13.982622382244227 -35.21861238909675 L-9.982622382244223 -28.29040915882124 A30 30 0 0 1 -0.29529320655473523 -29.998546663499596 L-0.29529320655473523 -29.998546663499596 L1.0938922147807042 -37.87700868759726 L11.926777497915003 -35.96687873326103 L10.537592076579564 -28.08841670916336 A30 30 0 0 1 19.056486383928977 -23.170030787617215 L19.056486383928977 -23.170030787617215 L25.1848419288808 -28.312331665109532 L32.25550563543274 -19.885842790800766 L26.127150090480917 -14.743541913308448 A30 30 0 0 1 29.491524206117255 -5.500000000000013 M0 -20A20 20 0 1 0 0 20 A20 20 0 1 0 0 -20"
                              fill="#ed2024"
                            ></path>
                          </g>
                        </g>
                      </svg>
                      <p className={styles.loadingText}>Loading</p>
                    </button>
                  </>
                ) : error ? (
                  <div className={styles.errorContainer}>
                    <p>Error Loading Brands</p>
                  </div>
                ) : (
                  brands &&
                  brands
                    .sort((a, b) => a.acf.order - b.acf.order)
                    .map((brand) => (
                      <button
                        id={brand.id}
                        key={brand.id}
                        className={`${styles.brand} ${
                          selectedBrandId === brand.id ? styles.active : ""
                        }`}
                        onClick={() => handleBrandSelect(brand.id)}
                        title="Our Dealers sell a wide range of KYB Factory Kit Suspension as well as Racing and Genuine parts."
                      >
                        <img
                          src={brand.acf.brand_logo.sizes.medium}
                          alt={brand.acf.brand_logo.alt}
                          height={brand.acf.brand_logo.sizes["medium-height"]}
                          width={brand.acf.brand_logo.sizes["medium-width"]}
                        />
                      </button>
                    ))
                )}
              </div>
              {errorMessage !== "" ? (
                <p className={styles.brandErrorMessage}>{errorMessage}</p>
              ) : (
                ""
              )}
              {/* User Location Input */}
              {mapLoaded && (
                <div
                  className={`${styles.searchContainer} ${
                    visibleDealers === null || visibleDealers.length <= 0
                      ? styles.disabled
                      : ""
                  }`}
                >
                  <Autocomplete
                    onPlaceChanged={handlePlaceChanged}
                    onLoad={onLoadAutocomplete}
                  >
                    <input
                      type="text"
                      placeholder="Enter location here"
                      disabled={
                        visibleDealers === null || visibleDealers.length <= 0
                      }
                      onKeyDown={handleKeyDown}
                      required
                    />
                  </Autocomplete>
                  <button
                    type="button"
                    disabled={!searchQuery}
                    onClick={handleSearchClick}
                  >
                    <img
                      src={magnifyingGlassIcon}
                      alt="Magnifying glass icon"
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </CSSTransition>

      <CSSTransition
        nodeRef={dealerResultsNodeRef}
        in={dealerResultsInProp}
        timeout={1000}
        classNames={{
          appear: styles.dealerResultsAppear,
          appearActive: styles.dealerResultsActiveAppear,
          appearDone: styles.dealerResultsDoneAppear,
          enter: styles.dealerResultsEnter,
          enterActive: styles.dealerResultsActiveEnter,
          enterDone: styles.dealerResultsDoneEnter,
          exit: styles.dealerResultsExit,
          exitActive: styles.dealerResultsActiveExit,
          exitDone: styles.dealerResultsDoneExit,
        }}
        unmountOnExit={true}
        mountOnEnter={true}
        onExited={() => setDealerSearchInProp(true)}
      >
        <div className={styles.mainContainer}>
          <div
            className={styles.dealerResultsContainer}
            ref={dealerResultsNodeRef}
          >
            <p className={styles.currentLocationText}>Current Location</p>
            <div
              className={`${styles.searchContainer} ${
                selectedBrandId === null ? styles.disabled : ""
              }`}
              disabled={selectedBrandId === null}
            >
              <Autocomplete
                onPlaceChanged={handlePlaceChanged}
                onLoad={onLoadAutocomplete}
              >
                <input
                  type="text"
                  placeholder={searchQuery?.formatted_address}
                  disabled={selectedBrandId === null}
                  onKeyDown={handleKeyDown}
                  required
                />
              </Autocomplete>
              <button
                type="button"
                disabled={selectedBrandId === null}
                onClick={handleSearchClick}
              >
                <img src={magnifyingGlassIcon} alt="Magnifying glass icon" />
              </button>
            </div>
            <p className={styles.currentBrand}>
              Search results for{" "}
              {selectedBrandId && brands
                ? brands.filter((brand) => brand.id === selectedBrandId)[0]
                    .title.rendered
                : ""}{" "}
              <span onClick={handleChangeBrandClick}>(Change Brand)</span>
            </p>
          </div>
          <hr className={styles.sectionSeparator} />
          <div className={styles.availableDealersSection}>
            {markers &&
              markers.map((marker, index) => (
                <div className={styles.dealerDetails} key={index}>
                  <button
                    className={styles.dealerIdentifier}
                    onClick={() => handleViewMapClick(marker)}
                  >
                    {marker.label}
                  </button>
                  <p
                    className={styles.dealerName}
                    dangerouslySetInnerHTML={{ __html: marker.dealerName }}
                  ></p>
                  <p className={styles.dealerAddress}>{marker.address}</p>
                  <p className={styles.dealerContactNumber}>
                    <a href={`tel:${marker.contactNumber.split(" ").join("")}`}>
                      {marker.contactNumber}
                    </a>
                  </p>
                  <p className={styles.dealerEmailAddress}>
                    <a href={`mailto:${marker.email}`}>{marker.email}</a>
                  </p>
                  <p className={styles.dealerWebsite}>
                    <a href={marker.website} target="_blank" rel="noreferrer">
                      {marker.website}
                    </a>
                  </p>
                  <div className={styles.dealerStats}>
                    <p className={styles.distance}>
                      {marker.distance
                        ? Math.round(marker.distance / 1000)
                        : ""}
                      Km
                    </p>
                    <button
                      className={styles.mapViewButton}
                      onClick={() => handleViewMapClick(marker)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="26"
                        width="19"
                        viewBox="0 0 384 512"
                      >
                        <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                      </svg>
                      View on map
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div ref={mapContainerRef} className={styles.mapContainer}>
            <GoogleMap
              mapContainerClassName={styles.mapContainer}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
              zoom={10}
              onLoad={onLoadMap}
            >
              {markers &&
                markers.map((marker, index) => (
                  <MarkerF
                    key={index}
                    position={{
                      lat: marker.lat,
                      lng: marker.lng,
                    }}
                    label={marker.label}
                    onClick={(e) => {
                      mapRef.current.panTo(e.latLng);
                      setActiveMarker(index);
                    }}
                  >
                    {activeMarker === index ? (
                      <InfoWindowF
                        position={{ lat: 0, lng: 0 }}
                        anchor={marker}
                        options={{ isOpen: true }}
                        onCloseClick={() => setActiveMarker(null)}
                      >
                        <div className={styles.infoWindow}>
                          <p className={styles.name}>{marker.dealerName}</p>
                          <i
                            className="fa-solid fa-location-dot"
                            style={{ color: "#888", marginBottom: "0.25rem" }}
                          ></i>
                          {marker.address.split(", ").map((addressPart) => (
                            <p className={styles.address}>{addressPart}</p>
                          ))}
                          {marker.email && (
                            <p className={styles.email}>
                              <a href={`mailto:${marker.email}`}>
                                <i
                                  className="fa-solid fa-at"
                                  style={{ color: "#888" }}
                                ></i>{" "}
                                {marker.email}
                              </a>
                            </p>
                          )}
                          {marker.website && (
                            <p className={styles.website}>
                              <a
                                href={marker.website}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i
                                  className="fa-solid fa-globe"
                                  style={{ color: "#888" }}
                                ></i>{" "}
                                {marker.website}
                              </a>
                            </p>
                          )}
                          {marker.contactNumber && (
                            <p className={styles.contactNumber}>
                              <a href={`tel:${marker.contactNumber}`}>
                                <i
                                  className="fa-solid fa-phone"
                                  style={{ color: "#888" }}
                                ></i>{" "}
                                {marker.contactNumber}
                              </a>
                            </p>
                          )}
                        </div>
                      </InfoWindowF>
                    ) : null}
                  </MarkerF>
                ))}
              {searchQuery && (
                <MarkerF
                  position={{
                    lat: searchQuery.geometry.location.lat(),
                    lng: searchQuery.geometry.location.lng(),
                  }}
                  icon={{
                    url: customLocationPin,
                    scaledSize: new window.google.maps.Size(26, 37),
                  }}
                />
              )}
            </GoogleMap>
          </div>
        </div>
      </CSSTransition>
    </div>
  );
};
export default DealerLocator;
